import { X } from "lucide-react";
import type { TradingAlert } from "./types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TooltipIconButton } from "@/components/trading/TooltipIconButton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface AlertBarProps {
  alert: TradingAlert | null;
  now: number;
  onDismiss: (id: string) => void;
  onJump: (alert: TradingAlert) => void;
}

const SEVERITY_DESCRIPTIONS: Record<number, string> = {
  1: "Severity 1 — Informational. Low priority.",
  2: "Severity 2 — Notable. Worth a glance.",
  3: "Severity 3 — Significant. Pay attention.",
  4: "Severity 4 — High priority. Possibly market-relevant.",
  5: "Severity 5 — Highest priority. Likely market-moving.",
};

function relativeAge(now: number, createdAt: number): string {
  const seconds = Math.max(0, Math.floor((now - createdAt) / 1_000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
}

function severityTooltip(severity: number | string): string {
  const numeric = typeof severity === "number" ? severity : Number(severity);
  if (Number.isFinite(numeric) && numeric in SEVERITY_DESCRIPTIONS) {
    return SEVERITY_DESCRIPTIONS[numeric];
  }
  return `Severity ${severity}`;
}

export function AlertBar({ alert, now, onDismiss, onJump }: AlertBarProps) {
  if (!alert) return null;

  return (
    <aside
      aria-live="polite"
      className="flex items-center gap-3 min-h-[42px] border-y border-destructive/40 bg-destructive/10 px-4 py-2"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="destructive" className="text-xs font-bold cursor-help">
            {alert.severity}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{severityTooltip(alert.severity)}</TooltipContent>
      </Tooltip>
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {alert.pane.toUpperCase()} · {alert.materialId}
      </span>
      <span className="flex-1 min-w-0 truncate text-sm">{alert.title}</span>
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {relativeAge(now, alert.createdAt)}
      </span>
      <Button size="sm" onClick={() => onJump(alert)}>
        Jump
      </Button>
      <TooltipIconButton label="Dismiss alert" onClick={() => onDismiss(alert.id)}>
        <X className="h-4 w-4" />
      </TooltipIconButton>
    </aside>
  );
}
